import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  Chip,
  Switch,
  FormControlLabel,
  Stack,
  MenuItem,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Plus } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useNotify } from 'react-admin';
import { NarrativeForm, logger } from '@musedrops/shared';
import {
  listAllNarrativeForms,
  createNarrativeForm,
  updateNarrativeForm,
  deleteNarrativeForm,
  countStoriesWithForm,
} from '../../../data/narrativeForms';
// getGenres (not getAllGenres): the genres table has no `enabled` column, so
// the onlyEnabled filter in getAllGenres errors. We want all genres anyway.
import { getGenres } from '../../../data/genre';
import { getAllShowDynamics } from '../../../data/showDynamics';

interface FormWithUsage extends NarrativeForm {
  storyCount: number;
}

const KEY_PATTERN = /^[a-z0-9-]+$/;

// Sentinel for the "All genres" filter option.
const ALL_GENRES = '__all__';

// A form counts for genre G if it has no compatible_genres restriction
// (null/empty = "all genres") or explicitly includes G.
const formMatchesGenre = (form: NarrativeForm, genre: string): boolean => {
  const compat = form.compatibleGenres;
  return !compat || compat.length === 0 || compat.includes(genre);
};

export const NarrativeFormsSettings: React.FC = () => {
  const notify = useNotify();
  const [searchParams, setSearchParams] = useSearchParams();
  const [forms, setForms] = useState<FormWithUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Genre filter above the table. Initialized from the ?genre= URL param
  // (used by the Genres tab deep-links); ALL_GENRES = show everything.
  const [genreFilter, setGenreFilter] = useState<string>(
    () => searchParams.get('genre') || ALL_GENRES
  );

  // Compatibility option sources
  const [genreNames, setGenreNames] = useState<string[]>([]);
  const [dynamicsKeys, setDynamicsKeys] = useState<string[]>([]);

  // Dialog state - shared for create and edit
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editingForm, setEditingForm] = useState<FormWithUsage | null>(null);
  const [dialogKey, setDialogKey] = useState('');
  const [dialogName, setDialogName] = useState('');
  const [dialogInstructions, setDialogInstructions] = useState('');
  const [dialogRoles, setDialogRoles] = useState('');
  const [dialogGenres, setDialogGenres] = useState<string[]>([]);
  const [dialogDynamics, setDialogDynamics] = useState<string[]>([]);
  const [dialogEnabled, setDialogEnabled] = useState(true);
  const [dialogWeight, setDialogWeight] = useState('1');

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingForm, setDeletingForm] = useState<FormWithUsage | null>(null);

  useEffect(() => {
    loadForms();
    loadOptions();
  }, []);

  const loadForms = async () => {
    try {
      setLoading(true);
      setError(null);

      const allForms = await listAllNarrativeForms();

      // Best-effort usage counts for each form
      const formsWithUsage = await Promise.all(
        allForms.map(async form => ({
          ...form,
          storyCount: await countStoriesWithForm(form.key),
        }))
      );

      setForms(formsWithUsage);
    } catch (err) {
      logger.error('Failed to load narrative forms', { error: err });
      setError(
        err instanceof Error ? err.message : 'Failed to load narrative forms'
      );
    } finally {
      setLoading(false);
    }
  };

  const loadOptions = async () => {
    // Independent loads: a failure in one must not blank the other.
    try {
      const genres = await getGenres();
      setGenreNames(genres.map(g => g.name));
    } catch (err) {
      logger.error('Failed to load genre options', { error: err });
    }
    try {
      const dynamics = await getAllShowDynamics();
      setDynamicsKeys(dynamics.map(d => d.key));
    } catch (err) {
      logger.error('Failed to load dynamics options', { error: err });
    }
  };

  const resetDialogFields = () => {
    setDialogKey('');
    setDialogName('');
    setDialogInstructions('');
    setDialogRoles('');
    setDialogGenres([]);
    setDialogDynamics([]);
    setDialogEnabled(true);
    setDialogWeight('1');
  };

  const handleCreateClick = () => {
    setDialogMode('create');
    setEditingForm(null);
    resetDialogFields();
    setDialogOpen(true);
  };

  const handleEditClick = (form: FormWithUsage) => {
    setDialogMode('edit');
    setEditingForm(form);
    setDialogKey(form.key);
    setDialogName(form.name);
    setDialogInstructions(form.instructions);
    setDialogRoles(form.roles || '');
    setDialogGenres(form.compatibleGenres || []);
    setDialogDynamics(form.compatibleDynamics || []);
    setDialogEnabled(form.enabled);
    setDialogWeight(String(form.weight ?? 1));
    setDialogOpen(true);
  };

  const keyValid = KEY_PATTERN.test(dialogKey);
  const parsedWeight = parseInt(dialogWeight, 10);
  const weightValid = Number.isFinite(parsedWeight);
  const canSave =
    keyValid && !!dialogName && !!dialogInstructions && weightValid;

  const buildPayload = () => ({
    key: dialogKey,
    name: dialogName,
    instructions: dialogInstructions,
    roles: dialogRoles || null,
    // Empty selection = NULL, meaning "all genres" / "all dynamics"
    compatibleGenres: dialogGenres.length > 0 ? dialogGenres : null,
    compatibleDynamics: dialogDynamics.length > 0 ? dialogDynamics : null,
    enabled: dialogEnabled,
    weight: weightValid ? parsedWeight : 1,
  });

  const handleDialogSave = async () => {
    if (dialogMode === 'create') {
      try {
        await createNarrativeForm(buildPayload());
        notify('Narrative form created successfully', { type: 'success' });
        setDialogOpen(false);
        resetDialogFields();
        loadForms();
      } catch (err) {
        notify(
          err instanceof Error ? err.message : 'Failed to create narrative form',
          { type: 'error' }
        );
      }
    } else {
      if (!editingForm) return;

      try {
        await updateNarrativeForm(editingForm.id, buildPayload());
        notify('Narrative form updated successfully', { type: 'success' });
        setDialogOpen(false);
        setEditingForm(null);
        resetDialogFields();
        loadForms();
      } catch (err) {
        notify(
          err instanceof Error ? err.message : 'Failed to update narrative form',
          { type: 'error' }
        );
      }
    }
  };

  const handleDeleteClick = (form: FormWithUsage) => {
    setDeletingForm(form);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingForm) return;

    try {
      await deleteNarrativeForm(deletingForm.id);
      notify('Narrative form deleted successfully', { type: 'success' });
      setDeleteDialogOpen(false);
      setDeletingForm(null);
      loadForms();
    } catch (err) {
      notify(
        err instanceof Error ? err.message : 'Failed to delete narrative form',
        { type: 'error' }
      );
    }
  };

  const renderCompatList = (values?: string[] | null) => {
    if (!values || values.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          All
        </Typography>
      );
    }
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {values.map(v => (
          <Chip key={v} label={v} size="small" />
        ))}
      </Box>
    );
  };

  const handleGenreFilterChange = (value: string) => {
    setGenreFilter(value);
    // Keep the URL in sync so the filtered view is shareable/deep-linkable.
    const next = new URLSearchParams(searchParams);
    if (value === ALL_GENRES) {
      next.delete('genre');
    } else {
      next.set('genre', value);
    }
    setSearchParams(next, { replace: true });
  };

  const visibleForms =
    genreFilter === ALL_GENRES
      ? forms
      : forms.filter(form => formMatchesGenre(form, genreFilter));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}
      >
        <TextField
          select
          size="small"
          label="Filter by genre"
          value={genreFilter}
          onChange={e => handleGenreFilterChange(e.target.value)}
          sx={{ minWidth: 240 }}
        >
          <MenuItem value={ALL_GENRES}>All genres</MenuItem>
          {genreNames.map(name => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="body2" color="text.secondary">
          {visibleForms.length} form{visibleForms.length === 1 ? '' : 's'}
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Key</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Genres</TableCell>
              <TableCell>Dynamics</TableCell>
              <TableCell align="center">Enabled</TableCell>
              <TableCell align="center">Weight</TableCell>
              <TableCell align="center">
                <IconButton
                  onClick={handleCreateClick}
                  color="primary"
                  size="small"
                >
                  <Plus size={18} />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleForms.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {forms.length === 0
                      ? 'No narrative forms defined yet. Click + to create one.'
                      : 'No narrative forms match this genre.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              visibleForms.map(form => (
                <TableRow
                  key={form.id}
                  onClick={() => handleEditClick(form)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <TableCell>
                    <code>{form.key}</code>
                  </TableCell>
                  <TableCell>{form.name}</TableCell>
                  <TableCell>{renderCompatList(form.compatibleGenres)}</TableCell>
                  <TableCell>
                    {renderCompatList(form.compatibleDynamics)}
                  </TableCell>
                  <TableCell align="center">
                    {form.enabled ? 'Yes' : 'No'}
                  </TableCell>
                  <TableCell align="center">{form.weight}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        handleDeleteClick(form);
                      }}
                      disabled={form.storyCount > 0}
                      title={
                        form.storyCount > 0
                          ? `Cannot delete - used by ${form.storyCount} story(ies)`
                          : 'Delete narrative form'
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {dialogMode === 'edit'
            ? 'Edit Narrative Form'
            : 'Create Narrative Form'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Key"
            fullWidth
            value={dialogKey}
            onChange={e => setDialogKey(e.target.value)}
            error={!!dialogKey && !keyValid}
            helperText="Unique kebab-case identifier, e.g., 'slow-reveal' (lowercase, digits, hyphens)"
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Name"
            fullWidth
            value={dialogName}
            onChange={e => setDialogName(e.target.value)}
            helperText="Human-readable name"
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Instructions"
            fullWidth
            multiline
            rows={6}
            value={dialogInstructions}
            onChange={e => setDialogInstructions(e.target.value)}
            helperText="Prompt-ready gestalt describing the whole narrative form"
            required
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            label="Roles (Optional)"
            fullWidth
            value={dialogRoles}
            onChange={e => setDialogRoles(e.target.value)}
            helperText="Abstract role pair the form implies, if any — e.g. 'proposer/resolver', 'narrator/doubter'. Mapped to real speakers by the show's dynamics at script time. Optional."
            sx={{ mb: 2 }}
          />

          <Autocomplete
            multiple
            options={genreNames}
            value={dialogGenres}
            onChange={(_e, newValue) => setDialogGenres(newValue)}
            renderTags={(value: string[], getTagProps) =>
              value.map((option: string, index: number) => (
                <Chip label={option} {...getTagProps({ index })} key={option} />
              ))
            }
            renderInput={params => (
              <TextField
                {...params}
                label="Compatible Genres"
                helperText="Leave empty for all genres"
              />
            )}
            sx={{ mb: 2 }}
          />

          <Autocomplete
            multiple
            options={dynamicsKeys}
            value={dialogDynamics}
            onChange={(_e, newValue) => setDialogDynamics(newValue)}
            renderTags={(value: string[], getTagProps) =>
              value.map((option: string, index: number) => (
                <Chip label={option} {...getTagProps({ index })} key={option} />
              ))
            }
            renderInput={params => (
              <TextField
                {...params}
                label="Compatible Dynamics"
                helperText="Leave empty for all dynamics"
              />
            )}
            sx={{ mb: 2 }}
          />

          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              margin="dense"
              label="Weight"
              type="number"
              value={dialogWeight}
              onChange={e => setDialogWeight(e.target.value)}
              error={!!dialogWeight && !weightValid}
              helperText="Selection weight (integer)"
              sx={{ width: 160 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={dialogEnabled}
                  onChange={e => setDialogEnabled(e.target.checked)}
                />
              }
              label="Enabled"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDialogSave}
            variant="contained"
            disabled={!canSave}
          >
            {dialogMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Narrative Form</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the narrative form{' '}
            <code>{deletingForm?.key}</code>?
          </Typography>
          {deletingForm && deletingForm.storyCount > 0 && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              This form is used by {deletingForm.storyCount} story(ies) and
              cannot be deleted.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={!!deletingForm && deletingForm.storyCount > 0}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
